/**
 * The guided first-run product walkthrough, built on react-joyride.
 *
 * - Spotlight overlay + branded tooltips (Solandio cream/indigo tokens).
 * - Steps attach to stable `data-tour="…"` targets added across the shell.
 * - Accessible: react-joyride manages focus and ARIA; Escape closes the tour;
 *   all control labels are localized. Targets that aren't currently mounted
 *   (e.g. the per-child switcher on a family-wide module, or the parent-only
 *   "Family" nav item for a trusted member) are dropped from the run so the
 *   tour never stalls on a missing element.
 * - prefers-reduced-motion: we feed Joyride a non-smooth scroll and the global
 *   stylesheet already neutralises transitions for those users.
 * - Mobile: targets live in the persistent header / sidebar which stay reachable
 *   on small screens; scrolling brings off-screen targets into view.
 *
 * No business logic — it only reads run-state from <TourProvider> and the
 * localized copy from i18n.
 */
import { useEffect, useMemo, useState } from 'react';
import Joyride, {
  STATUS,
  type CallBackProps,
  type Step,
  type Styles,
} from 'react-joyride';
import { useI18n } from '../i18n';
import { useTour } from './TourContext';

/** Targets that may be absent depending on route / role, in step order. */
interface TourStepDef {
  /**
   * Ordered candidate selectors; the first *visible* one wins. Lets a step
   * anchor to the desktop sidebar OR the mobile hamburger, whichever is on
   * screen. `'center'` is a screen-centered, un-anchored step.
   */
  targets: readonly string[];
  titleKey: keyof ReturnType<typeof useI18n>['t']['tour']['steps'];
  bodyKey: keyof ReturnType<typeof useI18n>['t']['tour']['steps'];
  placement?: Step['placement'];
}

const STEP_DEFS: readonly TourStepDef[] = [
  {
    targets: ['center'],
    titleKey: 'welcomeTitle',
    bodyKey: 'welcome',
    placement: 'center',
  },
  {
    targets: ['[data-tour="children"]'],
    titleKey: 'childrenTitle',
    bodyKey: 'children',
    placement: 'bottom',
  },
  {
    // Desktop: the persistent sidebar. Mobile: the hamburger that opens it.
    targets: ['[data-tour="modules"]', '[data-tour="modules-trigger"]'],
    titleKey: 'modulesTitle',
    bodyKey: 'modules',
    placement: 'auto',
  },
  {
    targets: ['[data-tour="family"]'],
    titleKey: 'familyTitle',
    bodyKey: 'family',
    placement: 'auto',
  },
  {
    targets: ['[data-tour="user-menu"]'],
    titleKey: 'settingsTitle',
    bodyKey: 'settings',
    placement: 'bottom',
  },
  {
    targets: ['center'],
    titleKey: 'finishTitle',
    bodyKey: 'finish',
    placement: 'center',
  },
];

/** Whether an element is laid out (not display:none / detached / zero-size). */
function isVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return true;
  const rects = el.getClientRects();
  return rects.length > 0 && el.offsetParent !== null;
}

/**
 * Resolves a step's first usable target selector: `'center'` always resolves;
 * otherwise the first candidate that exists AND is visible. Returns null when
 * none is on screen (the step is then dropped from the run).
 */
function resolveTarget(targets: readonly string[]): string | null {
  for (const sel of targets) {
    if (sel === 'center') return 'center';
    if (typeof document === 'undefined') continue;
    const el = document.querySelector(sel);
    if (el && isVisible(el)) return sel;
  }
  return null;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function OnboardingTour() {
  const { t } = useI18n();
  const { run, endTour } = useTour();

  // Resolve which steps are actually anchorable at the moment the tour starts,
  // so we never point at an element that isn't on screen for this user/route.
  const [steps, setSteps] = useState<Step[]>([]);

  useEffect(() => {
    if (!run) return;
    const resolved: Step[] = [];
    for (const def of STEP_DEFS) {
      const target = resolveTarget(def.targets);
      if (!target) continue; // not on screen for this user/route — skip it
      resolved.push({
        target,
        title: t.tour.steps[def.titleKey],
        content: t.tour.steps[def.bodyKey],
        placement: def.placement,
        disableBeacon: true,
      });
    }
    setSteps(resolved);
  }, [run, t]);

  const styles = useMemo<Partial<Styles>>(
    () => ({
      options: {
        primaryColor: 'var(--sol-primary)',
        textColor: 'var(--sol-ink)',
        backgroundColor: 'var(--sol-surface)',
        arrowColor: 'var(--sol-surface)',
        overlayColor: 'rgba(28, 36, 51, 0.55)',
        spotlightShadow: 'var(--sol-shadow-floating)',
        zIndex: 1000,
      },
      tooltip: {
        borderRadius: 'var(--sol-radius-card)',
        boxShadow: 'var(--sol-shadow-floating)',
        fontFamily: 'var(--sol-font-ui)',
        padding: '20px 20px 16px',
        maxWidth: 360,
      },
      tooltipContainer: { textAlign: 'left' },
      tooltipTitle: {
        fontFamily: 'var(--sol-font-display)',
        fontWeight: 700,
        fontSize: '1.05rem',
        color: 'var(--sol-ink)',
        margin: '0 0 6px',
      },
      tooltipContent: {
        padding: 0,
        fontSize: '0.95rem',
        lineHeight: 1.5,
        color: 'var(--sol-muted)',
      },
      spotlight: { borderRadius: 'var(--sol-radius-md)' },
      buttonNext: {
        backgroundColor: 'var(--sol-primary)',
        color: '#fff',
        borderRadius: 'var(--sol-radius-md)',
        fontWeight: 600,
        fontSize: '0.9rem',
        padding: '10px 16px',
        outline: 'none',
      },
      buttonBack: {
        color: 'var(--sol-muted)',
        fontWeight: 600,
        fontSize: '0.9rem',
        marginRight: 8,
      },
      buttonSkip: {
        color: 'var(--sol-muted)',
        fontSize: '0.85rem',
      },
      buttonClose: { color: 'var(--sol-muted)' },
    }),
    [],
  );

  function handleCallback(data: CallBackProps) {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      // Completed or dismissed: persist the done flag so it won't auto-start.
      endTour(true);
    }
  }

  if (!run || steps.length === 0) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      disableScrollParentFix
      scrollToFirstStep
      disableOverlayClose
      spotlightPadding={6}
      floaterProps={{ disableAnimation: prefersReducedMotion() }}
      locale={{
        back: t.tour.back,
        close: t.tour.close,
        last: t.tour.last,
        next: t.tour.next,
        skip: t.tour.skip,
      }}
      styles={styles}
      callback={handleCallback}
    />
  );
}
